import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea, Pill } from "../../components/UI";
import { Plus, HeartHandshake, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function FundraisingCampaigns() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", political_entity_id: "", goal_amount: 100000,
    hero_headline: "", hero_subline: "", story: "",
    cta_label: "Contribute", recurring_enabled: true,
    suggested_amounts: [10, 25, 50, 100, 250, 500], disclaimer: "",
  });

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/fundraising/campaigns"), api.get("/political-entities")]);
    setItems(c.data); setEntities(e.data);
    if (!form.political_entity_id && e.data[0]) setForm(f => ({ ...f, political_entity_id: e.data[0].id, disclaimer: e.data[0].approved_disclaimers?.[0] || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    await api.post("/fundraising/campaigns", {
      ...form,
      goal_amount: parseFloat(form.goal_amount) || 0,
      suggested_amounts: form.suggested_amounts,
    });
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/fundraising/campaigns/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Fundraising"
        title="Fundraising Campaigns"
        subtitle="Create branded fundraisers, drop them into landing pages, and promote them via political advertising."
        action={<Btn onClick={() => setOpen(true)} data-testid="new-fr-btn"><Plus className="w-4 h-4" /> New fundraiser</Btn>}
      />

      {items.length === 0 ? (
        <Card><div className="text-center py-10 text-sm text-slate-500">No fundraisers yet.</div></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map(c => {
            const pct = c.goal_amount ? Math.min(100, (c.raised_amount / c.goal_amount) * 100) : 0;
            return (
              <Card key={c.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold">{c.name}</div>
                    <div className="text-xs text-slate-400 mt-1 line-clamp-2">{c.hero_headline || c.story}</div>
                  </div>
                  <Pill status={c.status}>{c.status}</Pill>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>{usd(c.raised_amount)} of {usd(c.goal_amount)}</span>
                    <span>{c.total_donors} donors</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="mt-3 flex justify-between items-center">
                  <Btn variant="secondary" onClick={async () => {
                    try {
                      const r = await api.post(`/fundraising/campaigns/${c.id}/promote`);
                      toast.success(r.data.already_exists ? "Ad campaign already exists — opening Campaigns" : `Created draft CTV campaign: ${r.data.campaign.name}`);
                      navigate("/campaigns");
                    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
                  }} data-testid={`promote-${c.id}`}><Megaphone className="w-3.5 h-3.5" /> Promote via CTV</Btn>
                  <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">New fundraiser</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" data-testid="fr-name" />
              <Select label="Political entity" value={form.political_entity_id} onChange={(e) => {
                const ent = entities.find(x => x.id === e.target.value);
                setForm({ ...form, political_entity_id: e.target.value, disclaimer: ent?.approved_disclaimers?.[0] || form.disclaimer });
              }} className="col-span-2">
                <option value="">Select…</option>
                {entities.map(e => <option key={e.id} value={e.id}>{e.public_campaign_name || e.legal_org_name}</option>)}
              </Select>
              <Input label="Goal amount (USD)" type="number" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} />
              <Input label="CTA label" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
              <Input label="Hero headline" value={form.hero_headline} onChange={(e) => setForm({ ...form, hero_headline: e.target.value })} className="col-span-2" />
              <Textarea label="Story" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} className="col-span-2" />
              <Textarea label="Required disclaimer" value={form.disclaimer} onChange={(e) => setForm({ ...form, disclaimer: e.target.value })} className="col-span-2" />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="save-fr-btn">Create fundraiser</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
